import type { ApiResponse, User } from '@packages/shared-types';

import { apiClient } from '@/services/apiClient';

export interface ListUsersPaginatedParams {
  page: number;
  limit: number;
  tenantId?: string | null;
}

export interface ListUsersPaginatedResult {
  data: User[];
  total: number;
  page: number;
  limit: number;
}

export async function listUsersPaginated(
  params: ListUsersPaginatedParams,
): Promise<ListUsersPaginatedResult> {
  const { page, limit, tenantId } = params;
  const queryParams: Record<string, string | number> = { page, limit };
  if (tenantId) queryParams.tenantId = tenantId;
  const response = await apiClient.get<ApiResponse<ListUsersPaginatedResult>>(
    '/api/users',
    { params: queryParams },
  );
  const payload = response.data.data;
  return {
    data: payload.data,
    total: payload.total,
    page: payload.page,
    limit: payload.limit,
  };
}

export async function listUsers(tenantId?: string | null): Promise<User[]> {
  const params = tenantId ? { tenantId } : {};
  const response = await apiClient.get<ApiResponse<User[]>>('/api/users', {
    params,
  });
  return response.data.data;
}

export async function cancelInvitation(
  userId: string,
  tenantId?: string | null,
): Promise<void> {
  const params = tenantId ? { tenantId } : {};
  await apiClient.delete<ApiResponse<void>>(`/api/invitations/${userId}`, {
    params,
  });
}

export async function getUserById(
  id: string,
  tenantId?: string | null,
): Promise<User> {
  const params = tenantId ? { tenantId } : {};
  const response = await apiClient.get<ApiResponse<User>>(`/api/users/${id}`, {
    params,
  });
  return response.data.data;
}

export type UpdateUserPayload = {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
};

export async function updateUser(
  id: string,
  payload: UpdateUserPayload,
  tenantId?: string | null,
): Promise<User> {
  const params = tenantId ? { tenantId } : {};
  const response = await apiClient.put<ApiResponse<User>>(
    `/api/users/${id}`,
    payload,
    { params },
  );
  return response.data.data;
}

export async function deleteUser(
  id: string,
  tenantId?: string | null,
): Promise<void> {
  const params = tenantId ? { tenantId } : {};
  await apiClient.delete<ApiResponse<void>>(`/api/users/${id}`, { params });
}

export async function resetUserPassword(
  id: string,
  tenantId?: string | null,
): Promise<void> {
  const params = tenantId ? { tenantId } : {};
  await apiClient.post<ApiResponse<{ success: boolean }>>(
    `/api/users/${id}/reset-password`,
    {},
    { params },
  );
}

export async function toggleUserActive(
  id: string,
  tenantId?: string | null,
): Promise<User> {
  const params = tenantId ? { tenantId } : {};
  const response = await apiClient.post<ApiResponse<User>>(
    `/api/users/${id}/toggle-active`,
    {},
    { params },
  );
  return response.data.data;
}

export async function uploadUserImage(
  id: string,
  file: File,
  tenantId?: string | null,
): Promise<User> {
  const formData = new FormData();
  formData.append('image', file);

  const params = tenantId ? { tenantId } : {};
  const response = await apiClient.post<ApiResponse<User>>(
    `/api/users/${id}/upload-image`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params,
    },
  );

  return response.data.data;
}
